import React, { useState, useEffect, useCallback } from 'react'
import { Drawer, Empty, Spin, Tooltip, message, Button } from 'antd'
import { ArrowLeftOutlined, FolderFilled } from '@ant-design/icons'
import myDocumentStore from '../../stores/myDocumentStore'
import { MyDocumentRequest } from '../../requests/MyDocumentRequest'
import MyDocumentBreadcrumb from './MyDocumentBreadcrumb'
import { blue } from '../../color'

const MoveDocumentModal = ({ visible, onCancel, onSuccess, document }) => {
  const [loading, setLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [currentFolderId, setCurrentFolderId] = useState('')
  const [selectedTargetId, setSelectedTargetId] = useState(null)
  
  const [folderList, setFolderList] = useState([])
  const [breadcrumb, setBreadcrumb] = useState([])

  useEffect(() => {
    if (visible && document) {
      setCurrentFolderId('')
      setSelectedTargetId(null)
      loadFolders('')
    } else {
      setFolderList([])
      setBreadcrumb([])
    }
  }, [visible, document])

  const loadFolders = async (parentId) => {
    try {
      setIsFetching(true)
      const response = await MyDocumentRequest.getList({ size: 1000, type: 'folder', id: parentId })
      const folders = response.data?.data?.filter(item => item.type === 'folder' && item.id !== document.id) || []
      
      setFolderList(folders)
      setBreadcrumb(response.data?.breadcrumb || [])
    } catch (error) {
      console.error(error)
    } finally {
      setIsFetching(false)
    }
  }

  const handleNavigateToFolder = useCallback((folderId) => {
    setCurrentFolderId(folderId)
    setSelectedTargetId(null)
    loadFolders(folderId)
  }, [document])

  const handleGoBack = useCallback(() => {
    if (breadcrumb.length < 2) {
      handleNavigateToFolder('')
      return
    }
    const parentId = breadcrumb[breadcrumb.length - 2].id
    handleNavigateToFolder(parentId)
  }, [breadcrumb, handleNavigateToFolder])

  const handleSubmit = async () => {
    if (selectedTargetId === null) return
    try {
      setLoading(true)
      await myDocumentStore.moveDocument(document.id, selectedTargetId)
      message.success('Di chuyển thành công')
      onSuccess()
    } catch (error) {
      message.error('Lỗi khi di chuyển')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        if (!loading) onCancel()
      }}
      title={`Di chuyển "${document?.name}"`}
      maskClosable={false}
      destroyOnClose
      width={400}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel} disabled={loading}>
            Đóng
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading} disabled={selectedTargetId === null}>
            Di chuyển
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12 }}>
        {currentFolderId !== '' && (
          <Tooltip title="Quay lại">
            <Button icon={<ArrowLeftOutlined />} size="small" type="text" onClick={handleGoBack} />
          </Tooltip>
        )}
        <MyDocumentBreadcrumb
          breadcrumb={breadcrumb}
          onNavigate={handleNavigateToFolder}
        />
      </div>

      {isFetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      ) : folderList.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Thư mục trống" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {folderList.map(folder => {
            const isSelected = selectedTargetId === folder.id
            return (
              <div
                key={folder.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5'
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => setSelectedTargetId(folder.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FolderFilled style={{ color: '#faad14', fontSize: 18 }} />
                  <span style={{ 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    maxWidth: 200,
                    fontWeight: isSelected ? 500 : 400
                  }}>
                    {folder.name}
                  </span>
                </div>
                <Button
                  type="link"
                  size="small"
                  style={{ color: blue, padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNavigateToFolder(folder.id)
                  }}
                >
                  Mở
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </Drawer>
  )
}

export default MoveDocumentModal
